import { useState, useRef, useEffect } from "react";
import api, { API, TOKEN_KEY, IMPEXP_TENANT_KEY } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import JSZip from "jszip";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { UploadCloud, Download, Database, FileSpreadsheet, FileArchive, Loader2, CheckCircle2, Eye, RotateCcw, AlertTriangle, FolderUp } from "lucide-react";

const SectionCard = ({ title, icon: Icon, children, testId }) => (
  <div className="rounded-xl border border-slate-800 bg-slate-900/40 overflow-hidden" data-testid={testId}>
    <div style={{ backgroundColor: "#0f1e3d" }} className="px-5 py-3 flex items-center gap-2 text-white">
      <Icon className="h-4 w-4 text-amber-400" />
      <span className="text-sm font-bold uppercase tracking-widest">{title}</span>
    </div>
    <div className="p-5 space-y-4">{children}</div>
  </div>
);

function download(url, filename, params = {}) {
  const token = localStorage.getItem(TOKEN_KEY);
  const qs = new URLSearchParams(params).toString();
  const impT = sessionStorage.getItem(IMPEXP_TENANT_KEY);
  const headers = { Authorization: `Bearer ${token}`, ...(impT ? { "X-Impexp-Tenant": impT } : {}) };
  return fetch(`${API}${url}${qs ? `?${qs}` : ""}`, { headers })
    .then(async (r) => {
      if (!r.ok) {
        let msg = "Download non riuscito";
        try { msg = (await r.json()).detail || msg; } catch { /* ignore */ }
        throw new Error(msg);
      }
      const blob = await r.blob();
      if (!blob.size) throw new Error("Il server ha restituito un file vuoto");
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
    });
}

const UPLOAD_TIMEOUT_MS = 300 * 1000;
const CHUNK_SIZE = 50 * 1024 * 1024; // 50MB per chunk
const CHUNK_MAX_RETRIES = 3; // tentativi per singolo blocco prima di segnalare l'errore

// Invia un singolo blocco con retry automatico (fino a CHUNK_MAX_RETRIES) in caso di errore
// di rete/proxy (es. 502/520 Cloudflare transitorio) prima di propagare l'eccezione.
async function postChunkWithRetry(fd, onUploadProgress) {
  let lastErr;
  for (let attempt = 1; attempt <= CHUNK_MAX_RETRIES; attempt++) {
    try {
      return await api.post("/studio/impexp/upload/chunk", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: UPLOAD_TIMEOUT_MS,
        onUploadProgress,
      });
    } catch (e) {
      lastErr = e;
      if (attempt < CHUNK_MAX_RETRIES) await new Promise((r) => setTimeout(r, 800 * attempt));
    }
  }
  throw lastErr;
}

// Raggruppa i file: archivi multi-parte (name.tar.gz.001, .002, ...) diventano un unico job
function impProcPct(p) {
  if (!p) return 0;
  if (p.phase === "documenti" && p.files_total) return Math.min(99, Math.round((p.files_done / p.files_total) * 100));
  if (p.phase === "pratiche" && p.total_pratiche) return Math.min(99, Math.round((p.processed_pratiche / p.total_pratiche) * 100));
  if (p.phase === "completato") return 100;
  if (p.phase === "analisi") return 5;
  return 0;
}

function impProgressLabel(p) {
  if (!p) return "";
  if (p.phase === "in coda") return "In coda…";
  if (p.phase === "analisi") return `Analisi archivio… ${p.folders_found || 0} pratiche rilevate`;
  if (p.phase === "pratiche") return `Creazione pratiche: ${p.processed_pratiche || 0}/${p.total_pratiche || 0} elaborate (${p.imported || 0} importate${p.skipped ? `, ${p.skipped} saltate` : ""})`;
  if (p.phase === "documenti") return `Documenti e allegati: ${p.files_done || 0}/${p.files_total || 0} · ${p.imported || 0} pratiche importate`;
  if (p.phase === "completato") return `Completato: ${p.imported || 0} pratiche importate`;
  return "";
}

// Formattazione dimensioni per il riepilogo di upload/analisi.
function fmtSize(b) {
  if (b == null) return "—";
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
}
function fmtMB(b) {
  return `${((b || 0) / 1024 / 1024).toFixed(1)} MB`;
}

function buildUploadJobs(files) {
  const groups = {};
  const singles = [];
  for (const f of files) {
    const m = f.name.match(/^(.*)\.(\d{3})$/); // es. archivio.tar.gz.001
    if (m) {
      const base = m[1];
      (groups[base] = groups[base] || []).push(f);
    } else {
      singles.push(f);
    }
  }
  const jobs = [];
  for (const [base, parts] of Object.entries(groups)) {
    parts.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
    jobs.push({ label: base, parts });
  }
  for (const f of singles) jobs.push({ label: f.name, parts: [f] });
  return jobs;
}

// --- Supporto cartelle non zippate: le comprimiamo lato client in un unico .zip e le
// facciamo passare per lo stesso pipeline (upload a chunk + job asincrono) degli archivi. ---
function readDirEntries(dirEntry) {
  return new Promise((resolve, reject) => {
    const reader = dirEntry.createReader();
    const all = [];
    const readBatch = () => reader.readEntries((batch) => {
      if (!batch.length) { resolve(all); return; }
      all.push(...batch);
      readBatch();
    }, reject);
    readBatch();
  });
}

async function collectEntry(entry, prefix) {
  if (entry.isFile) {
    const file = await new Promise((res, rej) => entry.file(res, rej));
    return [{ file, path: prefix ? `${prefix}/${entry.name}` : entry.name }];
  }
  const children = await readDirEntries(entry);
  let out = [];
  for (const c of children) {
    out = out.concat(await collectEntry(c, prefix ? `${prefix}/${entry.name}` : entry.name));
  }
  return out;
}

async function zipEntriesToFile(entries, zipName) {
  const zip = new JSZip();
  for (const { file, path } of entries) zip.file(path, file);
  const blob = await zip.generateAsync({ type: "blob", compression: "STORE" });
  return new File([blob], zipName, { type: "application/zip" });
}

// Rileva se le entry (path che includono il nome della cartella radice) hanno un file
// 'dati_pratica' direttamente nella radice: in tal caso la radice È una singola pratica.
function rootHasDatiPratica(entries) {
  return entries.some(({ path }) => {
    const parts = path.split("/");
    return parts.length === 2 && parts[1].toLowerCase().startsWith("dati_pratica");
  });
}

function stripFirstSegment(entries) {
  return entries
    .map(({ file, path }) => {
      const parts = path.split("/");
      return { file, path: parts.length > 1 ? parts.slice(1).join("/") : parts[0] };
    })
    .filter((x) => x.path);
}

// Normalizza le entry di UNA cartella radice: se la radice contiene direttamente il file
// 'dati_pratica' è una singola pratica (le sottocartelle sono cartelle di allegati) e va
// mantenuta così com'è; altrimenti è un contenitore di più pratiche e si rimuove il segmento radice.
function normalizeRootEntries(entries) {
  return rootHasDatiPratica(entries) ? entries : stripFirstSegment(entries);
}

// Da una FileList di <input webkitdirectory>: path completo, con la cartella radice inclusa.
function entriesFromFileList(list) {
  return list
    .map((f) => ({ file: f, path: f.webkitRelativePath || f.name }))
    .filter((x) => x.path);
}

const StudioPicker = ({ value, onChange }) => {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/tenants").then(({ data }) => setList(Array.isArray(data) ? data : data?.items || [])).catch(() => setList([])); }, []);
  return (
    <div className="rounded-xl border-2 border-amber-500/50 bg-amber-500/5 p-4 flex flex-wrap items-center gap-3" data-testid="impexp-studio-picker">
      <Label className="text-sm text-amber-300 font-semibold">Studio su cui operare</Label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid="impexp-studio-select"
        className="h-10 min-w-[260px] rounded-md border border-slate-700 bg-slate-900 px-3 text-sm text-slate-100">
        <option value="">— Seleziona uno studio —</option>
        {list.map((t) => <option key={t.id} value={t.id}>{t.studio_name || t.id}</option>)}
      </select>
      {!value && <span className="text-xs text-slate-400">Come superadmin devi scegliere lo studio prima di esportare, fare backup o importare.</span>}
    </div>
  );
};

export default function ImportExport() {
  const { user } = useAuth();
  const isSA = user?.role === "superadmin";
  const [saTenant, setSaTenant] = useState(() => sessionStorage.getItem(IMPEXP_TENANT_KEY) || "");
  const pickTenant = (v) => { if (v) sessionStorage.setItem(IMPEXP_TENANT_KEY, v); else sessionStorage.removeItem(IMPEXP_TENANT_KEY); setSaTenant(v); };
  const [impexpOk, setImpexpOk] = useState(null);
  useEffect(() => { api.get("/studio/impexp/status").then(({ data }) => setImpexpOk(!!data.enabled)).catch(() => setImpexpOk(false)); }, []);
  const [files, setFiles] = useState([]);
  const [selStats, setSelStats] = useState(null);
  const [progress, setProgress] = useState(0);
  const [importing, setImporting] = useState(false);
  const [jobProgress, setJobProgress] = useState(null); // {phase, processed_pratiche, total_pratiche, imported, skipped, files_done, files_total}
  const [uploadStat, setUploadStat] = useState(null); // {name, sizeBytes, uploadedBytes, totalBytes}
  const [result, setResult] = useState(null); // {imported, total, errors}
  const [dfrom, setDfrom] = useState("");
  const [dto, setDto] = useState("");
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null); // {folders, count, errors}
  const [previewing, setPreviewing] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreMode, setRestoreMode] = useState("merge");
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const [allegatiZip, setAllegatiZip] = useState(null);
  const [linking, setLinking] = useState(false);
  const [linkResult, setLinkResult] = useState(null);
  const [zipping, setZipping] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const folderInputRef = useRef(null);
  const praticheStartRef = useRef(null);
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!importing) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [importing]);

  const runRestoreAllegati = async () => {
    if (!allegatiZip) { toast.error("Seleziona il file ZIP degli allegati"); return; }
    setLinking(true); setLinkResult(null);
    const fd = new FormData();
    fd.append("zipfile_up", allegatiZip);
    if (restoreFile) fd.append("manifest", restoreFile);
    try {
      const { data } = await api.post("/studio/impexp/restore-allegati", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLinkResult(data);
      toast.success(`Allegati ricollegati: ${data.linked}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Operazione non riuscita");
    } finally { setLinking(false); }
  };

  const runPreview = async () => {
    if (!files.length) { toast.error("Seleziona uno o più file ZIP"); return; }
    setPreviewing(true); setPreview(null); setResult(null);
    let folders = []; const errors = [];
    for (const f of files) {
      const fd = new FormData(); fd.append("file", f);
      try {
        const { data } = await api.post("/studio/impexp/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: UPLOAD_TIMEOUT_MS });
        folders = folders.concat((data.folders || []).map((x) => ({ ...x, _zip: f.name })));
        (data.errors || []).forEach((er) => errors.push(`${f.name}: ${er}`));
      } catch (e) {
        errors.push(`${f.name}: ${e.response?.data?.detail || e.message}`);
      }
    }
    setPreview({ folders, count: folders.length, errors });
    setPreviewing(false);
  };

  const runRestore = async () => {
    if (!restoreFile) { toast.error("Seleziona il file backup.json"); return; }
    if (restoreMode === "replace" && !window.confirm("ATTENZIONE: la modalità 'Sostituisci' elimina tutte le pratiche e anagrafiche esistenti prima del ripristino. Continuare?")) return;
    setRestoring(true); setRestoreResult(null);
    const fd = new FormData(); fd.append("file", restoreFile); fd.append("mode", restoreMode);
    try {
      const { data } = await api.post("/studio/impexp/restore", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRestoreResult(data.restored);
      toast.success(`Ripristino completato: ${data.restored.pratiche} pratiche, ${data.restored.anagrafiche} anagrafiche`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ripristino non riuscito");
    } finally { setRestoring(false); }
  };

  const pollImportJob = async (jobId, onProgress) => {
    // Interroga lo stato del job in background ogni 5s: elimina il timeout della singola richiesta
    for (;;) {
      await new Promise((r) => setTimeout(r, 5000));
      const { data } = await api.get(`/studio/impexp/import/status/${jobId}`);
      if (onProgress && data.progress) onProgress(data.progress);
      if (data.status === "done") return data.result || { imported: 0, total: 0, skipped: 0, errors: [] };
      if (data.status === "error") throw new Error(data.error || "Importazione non riuscita");
    }
  };

  const runImport = async () => {
    if (!files.length) { toast.error("Seleziona uno o più archivi"); return; }
    setImporting(true); setResult(null); setProgress(0); setJobProgress(null); setUploadStat(null); praticheStartRef.current = null;
    let totImported = 0, totAll = 0, totSkipped = 0; const allErrors = []; const allFolders = [];

    const jobs = buildUploadJobs(files);
    const grandTotal = jobs.reduce((s, j) => s + j.parts.reduce((a, f) => a + f.size, 0), 0) || 1;
    let uploadedGlobal = 0;

    for (const job of jobs) {
      const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      let chunkIndex = 0;
      try {
        // Carica ogni parte (in ordine) suddivisa in chunk da 50MB sotto lo stesso upload_id
        for (const part of job.parts) {
          setUploadStat({ name: part.name, sizeBytes: part.size, uploadedBytes: uploadedGlobal, totalBytes: grandTotal });
          for (let off = 0; off < part.size || (part.size === 0 && off === 0); off += CHUNK_SIZE) {
            const blob = part.slice(off, off + CHUNK_SIZE);
            const fd = new FormData();
            fd.append("upload_id", uploadId);
            fd.append("index", String(chunkIndex));
            fd.append("file", blob, `${part.name}.part${chunkIndex}`);
            const sent = blob.size;
            await postChunkWithRetry(fd, (e) => {
              const cur = e.total ? (e.loaded / e.total) * sent : 0;
              setProgress(Math.min(99, Math.round(((uploadedGlobal + cur) / grandTotal) * 100)));
              setUploadStat({ name: part.name, sizeBytes: part.size, uploadedBytes: uploadedGlobal + cur, totalBytes: grandTotal });
            });
            uploadedGlobal += sent;
            chunkIndex += 1;
            setProgress(Math.min(99, Math.round((uploadedGlobal / grandTotal) * 100)));
            setUploadStat({ name: part.name, sizeBytes: part.size, uploadedBytes: uploadedGlobal, totalBytes: grandTotal });
            if (part.size === 0) break;
          }
        }
        // Avvia l'import in background e attende via polling dello stato
        const cfd = new FormData();
        cfd.append("upload_id", uploadId);
        const { data: started } = await api.post("/studio/impexp/upload/complete", cfd, {
          headers: { "Content-Type": "multipart/form-data" }, timeout: UPLOAD_TIMEOUT_MS,
        });
        const data = await pollImportJob(started.job_id, (prog) => {
          if (prog.phase === "analisi") praticheStartRef.current = null;
          else if (prog.phase === "pratiche" && !praticheStartRef.current) praticheStartRef.current = { ts: Date.now(), processed: prog.processed_pratiche || 0 };
          setJobProgress((prev) => ({ ...prog, folders: prog.folders || prev?.folders }));
          setUploadStat((s) => ({ ...(s || {}), name: job.label }));
        });
        totImported += data.imported; totAll += data.total; totSkipped += (data.skipped || 0);
        (data.folder_details || (data.folders || []).map((n) => ({ name: n, status: "importata" }))).forEach((fd) => allFolders.push(fd));
        (data.errors || []).forEach((er) => allErrors.push(`${job.label}: ${er}`));
      } catch (e) {
        allErrors.push(`${job.label}: ${e.response?.data?.detail || e.message}`);
      }
    }
    setProgress(100);
    setResult({ imported: totImported, total: totAll, skipped: totSkipped, errors: allErrors, folders: allFolders });
    setImporting(false);
    toast.success(`Importazione completata: ${totImported} pratiche${totSkipped ? `, ${totSkipped} saltate` : ""}`);
  };

  const onFolderSelect = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    if (!list.length) return;
    const rootName = (list[0].webkitRelativePath || "cartella").split("/")[0] || "cartella";
    setZipping(true);
    try {
      const entries = normalizeRootEntries(entriesFromFileList(list));
      const folderCount = new Set(entries.map((x) => x.path.split("/")[0])).size;
      const zf = await zipEntriesToFile(entries, `${rootName}.zip`);
      setFiles([zf]); setSelStats({ archives: 0, folders: folderCount, files: list.length }); setPreview(null); setResult(null);
      toast.success(`Cartella "${rootName}" pronta (${list.length} file). Avvia l'importazione.`);
    } catch (err) { toast.error("Impossibile leggere la cartella selezionata"); }
    finally { setZipping(false); }
  };

  const processDropped = async (roots, plainFiles) => {
    setZipping(true);
    try {
      const archives = [];
      let zipEntries = [];
      let rootName = "cartella";
      let hasDir = false;
      if (roots) {
        for (const root of roots) {
          if (root.isFile) {
            const file = await new Promise((res, rej) => root.file(res, rej));
            archives.push(file);
          } else if (root.isDirectory) {
            hasDir = true;
            rootName = root.name || rootName;
            const rootEntries = await collectEntry(root, ""); // include il nome della cartella radice
            zipEntries = zipEntries.concat(normalizeRootEntries(rootEntries));
          }
        }
      } else if (plainFiles) {
        archives.push(...plainFiles);
      }
      const newFiles = [...archives];
      if (hasDir && zipEntries.length) newFiles.push(await zipEntriesToFile(zipEntries, `${rootName}.zip`));
      if (newFiles.length) {
        setFiles(newFiles); setSelStats({ archives: archives.length, folders: new Set(zipEntries.map((x) => x.path.split("/")[0])).size, files: zipEntries.length }); setPreview(null); setResult(null);
        toast.success(`${newFiles.length} elemento/i pronto/i. Avvia l'importazione.`);
      } else {
        toast.error("Nessun file o cartella valida rilevata");
      }
    } catch (err) { toast.error("Impossibile leggere gli elementi trascinati"); }
    finally { setZipping(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const items = e.dataTransfer.items;
    let roots = null;
    if (items && items.length && items[0].webkitGetAsEntry) {
      roots = [];
      for (const it of items) { const en = it.webkitGetAsEntry(); if (en) roots.push(en); }
    }
    const plainFiles = roots ? null : Array.from(e.dataTransfer.files || []);
    processDropped(roots, plainFiles);
  };

  const downloadLog = () => {
    if (!result?.errors?.length) return;
    const blob = new Blob([result.errors.join("\n")], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "log_errori_import.txt";
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(a.href);
  };

  const doZip = async (key, kind, params = {}) => {
    setBusy(key);
    try {
      const { data } = await api.post("/studio/impexp/dl-token", { kind, ...params });
      const a = document.createElement("a");
      a.href = `${API}/studio/impexp/dl/${data.token}`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Download avviato: lo ZIP si genera mentre scarica, segui l'avanzamento nei download del browser");
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Download non riuscito"); }
    finally { setTimeout(() => setBusy(""), 1500); }
  };

  const doDownload = async (key, url, filename, params) => {
    setBusy(key);
    try { await download(url, filename, params); }
    catch (e) { toast.error(e.message || "Download non riuscito"); }
    finally { setBusy(""); }
  };

  const ts = () => new Date().toISOString().slice(0, 16).replace(/[:T]/g, "");

  const fmtDuration = (s) => {
    if (s < 60) return `${s} second${s === 1 ? "o" : "i"}`;
    const m = Math.floor(s / 60), sec = s % 60;
    const mm = `${m} minut${m === 1 ? "o" : "i"}`;
    return sec ? `${mm} e ${sec} second${sec === 1 ? "o" : "i"}` : mm;
  };
  const etaText = (() => {
    const start = praticheStartRef.current;
    if (!jobProgress || jobProgress.phase !== "pratiche" || !start) return null;
    const processed = jobProgress.processed_pratiche || 0;
    const total = jobProgress.total_pratiche || 0;
    const done = processed - start.processed;
    const elapsed = (Date.now() - start.ts) / 1000;
    if (done <= 0 || elapsed <= 1 || total <= processed) return null;
    const rate = done / elapsed;
    const secs = Math.max(1, Math.round((total - processed) / rate));
    return fmtDuration(secs);
  })();

  const selLabel = (() => {
    if (!selStats) return `${files.length} file selezionati`;
    const parts = [];
    if (selStats.folders) parts.push(`${selStats.folders} ${selStats.folders === 1 ? "cartella" : "cartelle"}`);
    if (selStats.files) parts.push(`${selStats.files} file`);
    if (selStats.archives) parts.push(`${selStats.archives} ${selStats.archives === 1 ? "archivio" : "archivi"}`);
    return parts.length ? `${parts.join(" · ")} selezionat${parts.length === 1 && selStats.folders === 1 ? "a" : "i"}` : `${files.length} file selezionati`;
  })();

  if (impexpOk === null) return <div className="p-6 text-slate-400 text-sm" data-testid="impexp-loading"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Caricamento…</div>;
  if (!impexpOk) return (
    <div className="p-6" data-testid="impexp-disabled">
      <h1 className="font-head text-2xl font-bold text-slate-50">Import / Export & Backup</h1>
      <div className="mt-4 pgp-card p-5 flex items-start gap-3 max-w-2xl">
        <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
        <p className="text-sm text-slate-200" data-testid="impexp-disabled-msg">Sezione non disponibile. Contatta il supporto per attivare questa funzione.</p>
      </div>
    </div>
  );

  return (
    <div className="p-6 space-y-6" data-testid="impexp-page">
      <div>
        <h1 className="font-head text-2xl font-bold text-slate-50">Import / Export & Backup</h1>
        <p className="text-sm text-slate-400 mt-1">Importa pratiche da archivio esterno, esporta i tuoi dati o crea un backup completo.</p>
      </div>
      {isSA && <StudioPicker value={saTenant} onChange={pickTenant} />}
      {isSA && !saTenant ? null : (<>

      {/* IMPORT */}
      <SectionCard title="Importazione Pratiche da Archivio Esterno" icon={UploadCloud} testId="impexp-import">
        <p className="text-sm text-slate-400">
          Carica uno o più archivi ZIP, TAR, TAR.GZ o multi-parte (es. <code className="text-amber-400">archivio.tar.gz.001</code>, <code className="text-amber-400">.002</code>), oppure trascina direttamente una cartella non compressa. File grandi processati in modo asincrono in background a blocchi da 50MB. Struttura attesa: una cartella per pratica con il file <code className="text-amber-400">dati_pratica</code>. Le sottocartelle possono avere qualsiasi denominazione e vengono importate con il nome originale.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Input type="file" accept=".zip,.tar,.tar.gz,.tgz,.gz,.001,.002,.003,.004,.005" multiple data-testid="impexp-file-input"
            onChange={(e) => { const arr = Array.from(e.target.files || []); setFiles(arr); setSelStats({ archives: arr.length, folders: 0, files: 0 }); setPreview(null); setResult(null); }}
            className="bg-slate-900/60 border-slate-700 text-slate-100 flex-1 min-w-[240px]" />
          <input ref={folderInputRef} type="file" webkitdirectory="" directory="" multiple hidden
            onChange={onFolderSelect} data-testid="impexp-folder-input" />
          <Button type="button" variant="outline" onClick={() => folderInputRef.current?.click()} disabled={zipping || importing}
            data-testid="impexp-folder-btn" className="border-slate-700 text-slate-200 hover:bg-slate-800">
            <FolderUp className="h-4 w-4 mr-1" /> Seleziona cartella
          </Button>
        </div>
        <div onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={onDrop}
          data-testid="impexp-dropzone"
          className={`rounded-lg border-2 border-dashed px-4 py-8 text-center transition-colors ${dragOver ? "border-amber-500 bg-amber-500/10" : "border-slate-700 bg-slate-900/30"}`}>
          {zipping ? (
            <div className="flex items-center justify-center gap-2 text-sm text-slate-300"><Loader2 className="h-4 w-4 animate-spin" /> Preparazione cartella in corso…</div>
          ) : (
            <div className="flex flex-col items-center gap-2 text-slate-400">
              <UploadCloud className="h-6 w-6 text-slate-500" />
              <span className="text-sm">Trascina qui archivi ZIP/TAR/TAR.GZ o una cartella non compressa</span>
            </div>
          )}
        </div>
        {files.length > 0 && <div className="text-xs text-slate-400" data-testid="impexp-file-count">{selLabel}</div>}

        {importing && (
          <div className="space-y-1.5" data-testid="impexp-progress">
            <div className="h-2 w-full rounded-full bg-slate-800 overflow-hidden">
              <div className="h-full bg-amber-500 transition-all"
                style={{ width: `${jobProgress ? impProcPct(jobProgress) : progress}%` }} />
            </div>
            <div className="text-xs text-slate-400" data-testid="impexp-progress-label">
              {jobProgress ? impProgressLabel(jobProgress) : `Caricamento archivio… ${progress}%`}
            </div>
            {etaText && (
              <div className="text-xs text-amber-300/90" data-testid="impexp-eta">Tempo stimato: {etaText}</div>
            )}
            {jobProgress?.folders?.length > 0 && (
              <div className="mt-2 max-h-48 overflow-y-auto rounded-md border border-slate-800 bg-slate-950/40 p-2 space-y-0.5" data-testid="impexp-folder-list-progress">
                <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Cartelle rilevate ({jobProgress.folders.length})</div>
                {jobProgress.folders.map((fn, i) => {
                  const nm = typeof fn === "string" ? fn : fn.name;
                  return <div key={i} className="text-xs font-mono text-slate-300 truncate" title={nm} data-testid={`impexp-folder-row-${i}`}>{nm}</div>;
                })}
              </div>
            )}
            {uploadStat && (
              <div className="text-xs text-slate-500 space-y-0.5 pt-0.5" data-testid="impexp-upload-stat">
                <div data-testid="impexp-stat-name">Elaborazione: <span className="text-slate-300 font-medium">{uploadStat.name || "—"}</span></div>
                <div data-testid="impexp-stat-size">Dimensione: <span className="text-slate-300 font-mono">{fmtSize(uploadStat.sizeBytes)}</span></div>
                <div data-testid="impexp-stat-total">Totale: <span className="text-slate-300 font-mono">{fmtMB(uploadStat.uploadedBytes)} / {fmtMB(uploadStat.totalBytes)}</span></div>
              </div>
            )}
          </div>
        )}

        {result && (
          <div className="rounded-lg border border-slate-700 bg-slate-900/60 p-4 space-y-2" data-testid="impexp-result">
            <div className="flex items-center gap-2 text-white text-sm font-semibold">
              <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
              <span><span className="text-emerald-300 font-mono">{result.imported}</span> pratiche importate su <span className="font-mono text-white">{result.total}</span> cartelle</span>
            </div>
            {result.skipped > 0 && (
              <div className="flex items-center gap-2 text-sm text-slate-100" data-testid="impexp-skipped">
                <span className="inline-block h-2.5 w-2.5 rounded-full bg-amber-400 shrink-0" />
                <span><span className="text-amber-300 font-semibold">{result.skipped}</span> pratiche già presenti (stesso numero): saltate.</span>
              </div>
            )}
            {result.errors.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap text-sm text-slate-100">
                <span className="inline-block h-2.5 w-2.5 rounded-full bg-red-400 shrink-0" />
                <span><span className="text-red-300 font-semibold">{result.errors.length}</span> errori.</span>
                <button onClick={downloadLog} data-testid="impexp-download-log" className="underline underline-offset-2 font-medium text-cyan-300 hover:text-cyan-200">Scarica log errori</button>
              </div>
            )}
            {result.folders?.length > 0 && (
              <div className="mt-1 max-h-56 overflow-y-auto rounded-md border border-slate-800 bg-slate-950/40 p-2 space-y-1" data-testid="impexp-result-folder-list">
                <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Dettaglio cartelle ({result.folders.length})</div>
                {result.folders.map((f, i) => {
                  const name = typeof f === "string" ? f : f.name;
                  const status = typeof f === "string" ? "importata" : f.status;
                  const dot = status === "importata" ? "bg-emerald-400" : status === "saltata" ? "bg-amber-400" : "bg-red-400";
                  const lbl = status === "importata" ? "Importata" : status === "saltata" ? "Saltata" : "Errore";
                  const lblCls = status === "importata" ? "text-emerald-300" : status === "saltata" ? "text-amber-300" : "text-red-300";
                  return (
                    <div key={i} className="flex items-center gap-2 text-xs" data-testid={`impexp-result-folder-row-${i}`}>
                      <span className={`inline-block h-2 w-2 rounded-full shrink-0 ${dot}`} />
                      <span className="font-mono text-slate-300 truncate flex-1" title={name}>{name}</span>
                      {typeof f !== "string" && (
                        <span className="text-slate-500 shrink-0 font-mono" data-testid={`impexp-result-folder-counts-${i}`}>{f.allegati} all. · {f.lettere} lett.</span>
                      )}
                      <span className={`shrink-0 font-medium ${lblCls}`} data-testid={`impexp-result-folder-status-${i}`}>{lbl}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {preview && (
          <div className="rounded-lg border border-slate-800 bg-slate-900/50 p-4 space-y-3 max-h-80 overflow-y-auto" data-testid="impexp-preview">
            <div className="text-sm font-semibold text-slate-200">Anteprima: {preview.count} pratiche rilevate</div>
            {preview.folders.map((f, i) => (
              <div key={i} className="rounded-md border border-slate-800 p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-amber-300">{f.folder}</span>
                  <span className="text-xs text-slate-500">{f.cliente_files} all. cliente · {f.ctp_files} all. CTP</span>
                </div>
                {f.has_dati ? (
                  <div className="mt-2 grid sm:grid-cols-2 gap-x-4 gap-y-1">
                    {Object.entries(f.fields).map(([k, v]) => (
                      <div key={k} className="text-xs"><span className="text-slate-500">{k}:</span> <span className="text-slate-200">{String(v).slice(0, 80)}</span></div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-1 text-xs text-amber-300 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> file 'dati_pratica' mancante — verrà importata senza dati</div>
                )}
              </div>
            ))}
            {preview.errors.length > 0 && <div className="text-xs text-red-300">{preview.errors.length} avvisi rilevati.</div>}
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <Button onClick={runPreview} disabled={previewing || importing || !files.length} data-testid="impexp-preview-btn"
            className="bg-slate-800 text-white hover:bg-slate-700 border border-slate-700">
            {previewing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Eye className="h-4 w-4 mr-1" />} Anteprima
          </Button>
          <Button onClick={runImport} disabled={importing || !files.length} data-testid="impexp-import-btn"
            className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
            {importing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <UploadCloud className="h-4 w-4 mr-1" />} {preview ? "Conferma e importa" : "Avvia importazione"}
          </Button>
        </div>
      </SectionCard>

      {/* EXPORT */}
      <SectionCard title="Esportazione Pratiche" icon={Download} testId="impexp-export">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-slate-300 text-sm">Da (data apertura)</Label>
            <Input type="date" value={dfrom} onChange={(e) => setDfrom(e.target.value)} data-testid="impexp-from" className="mt-1.5 bg-slate-900/60 border-slate-700 text-slate-100" />
          </div>
          <div>
            <Label className="text-slate-300 text-sm">A (data apertura)</Label>
            <Input type="date" value={dto} onChange={(e) => setDto(e.target.value)} data-testid="impexp-to" className="mt-1.5 bg-slate-900/60 border-slate-700 text-slate-100" />
          </div>
        </div>
        <p className="text-xs text-slate-500">Lascia vuote le date per esportare tutte le pratiche.</p>
        <div className="flex flex-wrap gap-3">
          <Button onClick={() => doZip("zip", "export", { dfrom, dto })} disabled={busy === "zip"} data-testid="impexp-export-zip"
            className="bg-slate-800 text-white hover:bg-slate-700 border border-slate-700">
            {busy === "zip" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileArchive className="h-4 w-4 mr-1" />} Esporta ZIP (con allegati)
          </Button>
          <Button onClick={() => doDownload("xlsx", "/studio/impexp/export.xlsx", `pratiche_${ts()}.xlsx`, { dfrom, dto })} disabled={busy === "xlsx"} data-testid="impexp-export-xlsx"
            className="bg-slate-800 text-white hover:bg-slate-700 border border-slate-700">
            {busy === "xlsx" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1" />} Esporta Excel (.xlsx)
          </Button>
        </div>
      </SectionCard>

      {/* BACKUP */}
      <SectionCard title="Backup Completo Gestionale" icon={Database} testId="impexp-backup">
        <p className="text-sm text-slate-400">Scarica l'intero database in JSON (pratiche, anagrafiche, impostazioni studio, utenti) e tutti gli allegati in ZIP. Ogni file riporta data e ora nel nome.</p>
        <div className="flex flex-wrap gap-3">
          <Button onClick={() => doDownload("json", "/studio/impexp/backup.json", `backup_${ts()}.json`)} disabled={busy === "json"} data-testid="impexp-backup-json"
            className="bg-slate-800 text-white hover:bg-slate-700 border border-slate-700">
            {busy === "json" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Database className="h-4 w-4 mr-1" />} Backup dati (JSON)
          </Button>
          <Button onClick={() => doZip("all", "allegati")} disabled={busy === "all"} data-testid="impexp-backup-allegati"
            className="bg-slate-800 text-white hover:bg-slate-700 border border-slate-700">
            {busy === "all" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileArchive className="h-4 w-4 mr-1" />} Backup allegati (ZIP)
          </Button>
          <Button onClick={async () => { await doDownload("both", "/studio/impexp/backup.json", `backup_${ts()}.json`); await doZip("both", "allegati"); }} disabled={busy === "both"} data-testid="impexp-backup-both"
            className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
            {busy === "both" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />} Scarica tutto (JSON + ZIP)
          </Button>
        </div>
      </SectionCard>

      {/* RESTORE */}
      <SectionCard title="Ripristino da Backup" icon={RotateCcw} testId="impexp-restore">
        <p className="text-sm text-slate-400">Carica un file <code className="text-amber-400">backup.json</code> generato dal backup completo per ricostruire pratiche e anagrafiche. Gli allegati binari non sono inclusi nel JSON (usa il ZIP allegati separatamente).</p>
        <Input type="file" accept=".json,application/json" data-testid="impexp-restore-file"
          onChange={(e) => { setRestoreFile((e.target.files || [])[0] || null); setRestoreResult(null); }}
          className="bg-slate-900/60 border-slate-700 text-slate-100" />
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
            <input type="radio" name="restore-mode" value="merge" checked={restoreMode === "merge"} onChange={() => setRestoreMode("merge")} data-testid="restore-mode-merge" />
            Unisci (salta i duplicati)
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
            <input type="radio" name="restore-mode" value="replace" checked={restoreMode === "replace"} onChange={() => setRestoreMode("replace")} data-testid="restore-mode-replace" />
            Sostituisci (elimina e ricostruisci)
          </label>
        </div>
        {restoreMode === "replace" && (
          <div className="text-xs text-red-300 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Elimina tutte le pratiche e anagrafiche esistenti dello studio.</div>
        )}
        {restoreResult && (
          <div className="rounded-lg border border-slate-800 bg-slate-900/50 p-3 text-sm text-emerald-300 flex items-center gap-2" data-testid="impexp-restore-result">
            <CheckCircle2 className="h-4 w-4" /> Ripristinate {restoreResult.pratiche} pratiche e {restoreResult.anagrafiche} anagrafiche.
          </div>
        )}
        <Button onClick={runRestore} disabled={restoring || !restoreFile} data-testid="impexp-restore-btn"
          className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
          {restoring ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1" />} Avvia ripristino
        </Button>

        <div className="pt-4 mt-2 border-t border-slate-800 space-y-3">
          <div className="text-sm font-semibold text-slate-200">Ripristino allegati</div>
          <p className="text-xs text-slate-400">Carica il file ZIP degli allegati (dal backup) per ricollegarli alle pratiche. Il match usa il <code className="text-amber-400">backup.json</code> selezionato sopra (mappa numero pratica → allegati); senza di esso il match è meno affidabile. I duplicati con lo stesso nome vengono saltati.</p>
          <Input type="file" accept=".zip" data-testid="impexp-allegati-file"
            onChange={(e) => { setAllegatiZip((e.target.files || [])[0] || null); setLinkResult(null); }}
            className="bg-slate-900/60 border-slate-700 text-slate-100" />
          {linkResult && (
            <div className="rounded-lg border border-slate-800 bg-slate-900/50 p-3 text-sm text-emerald-300 flex items-center gap-2" data-testid="impexp-allegati-result">
              <CheckCircle2 className="h-4 w-4" /> Ricollegati {linkResult.linked} allegati{linkResult.skipped ? `, ${linkResult.skipped} saltati` : ""}.
            </div>
          )}
          <Button onClick={runRestoreAllegati} disabled={linking || !allegatiZip} data-testid="impexp-allegati-btn"
            className="bg-slate-800 text-white hover:bg-slate-700 border border-slate-700">
            {linking ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileArchive className="h-4 w-4 mr-1" />} Ripristina allegati
          </Button>
        </div>
      </SectionCard>
      </>)}
    </div>
  );
}
